/**
 * Platform Detection Utilities for Capacitor
 * Detects whether the app is running as a native mobile app or web
 * Uses multiple detection methods for reliability with live server mode
 */

import { Capacitor } from '@capacitor/core';

/**
 * Check if running on native platform (iOS or Android)
 * Uses multiple detection methods for reliability when loading from live server
 */
export function isNativePlatform(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    // Method 1: Capacitor's built-in detection (most reliable)
    if (Capacitor.isNativePlatform()) {
      return true;
    }

    // Method 2: Check for Capacitor bridge on window (injected in native WebView)
    if ((window as any).Capacitor?.isNativePlatform?.()) {
      return true;
    }

    // Method 3: Check for iOS WebKit message handlers (native bridge)
    if ((window as any).webkit?.messageHandlers?.bridge) {
      return true;
    }

    // Method 4: Check user agent for Capacitor/app markers
    const ua = navigator.userAgent;
    if (ua.includes('Capacitor') || ua.includes('InsuraCentral')) {
      return true;
    }

    // Method 5: Check if running in standalone mode (iOS PWA or native wrapper)
    if ((window.navigator as any).standalone === true) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Check if running on iOS
 * Uses Capacitor detection plus fallback user agent check
 */
export function isIOS(): boolean {
  if (typeof window === 'undefined') return false;

  // Primary: Capacitor detection
  if (Capacitor.getPlatform() === 'ios') return true;

  // Fallback: Check if native AND user agent indicates iOS
  if (isNativePlatform()) {
    const ua = navigator.userAgent;
    return /iPhone|iPad|iPod/.test(ua);
  }

  return false;
}

/**
 * Check if running on Android
 * Uses Capacitor detection plus fallback user agent check
 */
export function isAndroid(): boolean {
  if (typeof window === 'undefined') return false;

  // Primary: Capacitor detection
  if (Capacitor.getPlatform() === 'android') return true;

  // Fallback: Check if native AND user agent indicates Android
  if (isNativePlatform()) {
    const ua = navigator.userAgent;
    return /Android/.test(ua);
  }

  return false;
}

/**
 * Check if running on web
 */
export function isWeb(): boolean {
  return !isNativePlatform();
}

/**
 * Get the current platform name
 */
export function getPlatform(): 'ios' | 'android' | 'web' {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'web';
}

/**
 * Check if a plugin is available on the current platform
 */
export function isPluginAvailable(pluginName: string): boolean {
  return Capacitor.isPluginAvailable(pluginName);
}

/**
 * Platform information hook data
 */
export interface PlatformInfo {
  isNative: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isWeb: boolean;
  platform: 'ios' | 'android' | 'web';
}

/**
 * Get platform information object
 */
export function getPlatformInfo(): PlatformInfo {
  return {
    isNative: isNativePlatform(),
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isWeb: isWeb(),
    platform: getPlatform(),
  };
}

/**
 * Execute code only on native platforms
 */
export function onNative<T>(callback: () => T): T | undefined {
  if (isNativePlatform()) {
    return callback();
  }
  return undefined;
}

/**
 * Execute code only on iOS
 */
export function onIOS<T>(callback: () => T): T | undefined {
  if (isIOS()) {
    return callback();
  }
  return undefined;
}

/**
 * Execute code only on Android
 */
export function onAndroid<T>(callback: () => T): T | undefined {
  if (isAndroid()) {
    return callback();
  }
  return undefined;
}

/**
 * Execute code only on web
 */
export function onWeb<T>(callback: () => T): T | undefined {
  if (isWeb()) {
    return callback();
  }
  return undefined;
}
